import {
  Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, ViewChild,
  OnInit, OnDestroy, inject, effect, input
} from '@angular/core';
import {VestaRuntimeService} from '@app/vesta';
import {VestaMapperService} from '@app/vesta/services/vesta-mapper.service';
import {VestaStateService} from '@app/vesta/services/vesta-state.service';
import {ComposerStateService, ConnectionSide} from '@app/model-composer';
import {VestaCommandType} from '@app/vesta/types/vesta-command.types';
import {VestaBridgeService} from '@app/vesta/services/vesta-bridge.service';
import {ComposerViewMode} from '@app/model-composer/enums/view-mode.enum';

@Component({
  selector: 'app-vesta-composer',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    <visionnaire-composer #vestaElement lang="it" hide-powered-by="true"></visionnaire-composer>
  `,
  styles: [`
    :host { flex: 1; width: 100%; height: 100%; }
  `],
})
export class VestaComponent implements OnInit, OnDestroy {
  @ViewChild('vestaElement', { static: true })
  private vestaElement!: ElementRef<any>;
  public viewMode = input<ComposerViewMode>(ComposerViewMode.TwoD);
  private runtime = inject(VestaRuntimeService);
  private mapper = inject(VestaMapperService);
  private state = inject(VestaStateService);
  readonly composer = inject(ComposerStateService);
  private bridge = inject(VestaBridgeService);
  private removeListeners: Array<() => void> = [];
  private readonly _viewModeEffect = effect(() => {
    const vesta = this.vestaElement?.nativeElement;
    const viewMode = this.viewMode();
    if (!vesta || !viewMode) { return; }
    vesta.visualization = viewMode;
  });
  private readonly _updateEffect = effect(() => {
    const vesta = this.vestaElement?.nativeElement;
    if (!vesta) { return; }
    const queue = this.state.commandQueue();
    if (!queue.length) { return; }
    for (const cmd of queue) {
      switch (cmd.type) {
        case VestaCommandType.ViewUpdate: {
          if ('set' in cmd.payload) {
            vesta.configuration = {
              set: cmd.payload.set as any
            };
          } else {
            const config = this.mapper.toSetRemoveFromViewUpdate(cmd.payload);
            if (config) { vesta.configuration = config; }
          }
          break;
        }
      }
    }
    this.state.clearCommands();
  });

  ngOnInit(): void {
    this.runtime.init();
    this.runtime.whenReady(() => {
      const vesta = this.vestaElement.nativeElement;
      this.bridge.attach(vesta);
      this.initListeners(vesta);
    });
  }

  private initListeners(vesta: any): void {
    const on = (eventName: string, fn: (e: any) => void) => {
      vesta.addEventListener(eventName, fn);
      this.removeListeners.push(() => vesta.removeEventListener(eventName, fn));
    };
    on('snapshot', (event: any) => {
      const payload = event?.detail ?? event;
      this.state.snapshot.set(payload);
      this.state.setScreenshotFn(payload);
      this.state.pushUiEvent({ type: 'snapshot', payload });
    });
    on('composerInitialized', (event: any) => {
      this.state.initialized.set(true);
      this.state.pushUiEvent({ type: 'composerInitialized', payload: event?.detail ?? event });
    });
    on('composerReady', (event: any) => {
      this.state.ready.set(true);
      this.state.pushUiEvent({ type: 'composerReady', payload: event?.detail ?? event });
    });
    on('hookSelected', (event: any) => {
      const payload = event?.detail ?? event;
      // if (this.bridge.shouldIgnoreNextHookEvent()) { return; }
      const hook = payload?.hook;
      this.composer.openSelector(hook === 'A' ? ConnectionSide.Left : ConnectionSide.Right);
      this.state.pushUiEvent({ type: 'hookSelected', payload });
    });
    on('elementSelected', (event: any) => {
      const payload = event?.detail ?? event;
      this.composer.highlightItem(payload?.virtual_id);
      this.state.pushUiEvent({ type: 'elementSelected', payload });
    });
    on('allUnselected', (event: any) => {
      this.composer.resetSelection();
      this.state.pushUiEvent({ type: 'allUnselected', payload: event?.detail ?? event });
    });
    on('click', (event: any) => {
      const payload = event?.detail ?? event;
      if (typeof payload === 'function') {
        this.bridge.setExternalUpdateClicked(payload);
        return;
      }
      this.state.pushUiEvent({ type: 'click', payload });
    });
  }

  ngOnDestroy(): void {
    this.removeListeners.forEach(fn => fn());
    this.removeListeners = [];
    this.bridge.detach();
    this.vestaElement?.nativeElement?.remove?.();
    this.runtime.destroy();
    this.state.clearScreenshotFn();
  }
}
